import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Tag, Space, Modal, Input, AutoComplete, message, Form, Switch, Tooltip, Spin, Tabs } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, ReloadOutlined, FileTextOutlined, EditOutlined, PlusOutlined, DeleteOutlined, ClearOutlined } from '@ant-design/icons';
import { pluginApi } from '../services/api-services';

const { TextArea } = Input;

/** Shell-like split: respects single/double quoted strings */
function shellSplit(cmd: string): string[] {
  const result: string[] = [];
  let current = '';
  let quote = '';
  for (const ch of cmd) {
    if (quote) {
      if (ch === quote) { quote = ''; }
      else { current += ch; }
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === ' ') {
      if (current) { result.push(current); current = ''; }
    } else {
      current += ch;
    }
  }
  if (current) result.push(current);
  return result;
}

interface Gateway {
  name: string;
  working_dir: string;
  config_file: string;
  command: string[];
  auto_start: boolean;
  status: string;
  pid: number | null;
  started_at: string | null;
  restart_count: number;
}

const PluginManagement: React.FC = () => {
  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [loading, setLoading] = useState(false);
  const [builtins, setBuiltins] = useState<{ name: string; path: string; working_dir: string; default_command?: string; description?: string }[]>([]);
  const [logsModal, setLogsModal] = useState<{ visible: boolean; name: string; logs: string[] }>({ visible: false, name: '', logs: [] });
  const [configModal, setConfigModal] = useState<{ visible: boolean; name: string; globalConfigText: string; gw?: Gateway }>({ visible: false, name: '', globalConfigText: '' });
  const [editForm] = Form.useForm();
  const [addModal, setAddModal] = useState(false);
  const [selectedDesc, setSelectedDesc] = useState('');
  const [form] = Form.useForm();

  const fetchGateways = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await pluginApi.list();
      setGateways(res.gateways || []);
    } catch {
      message.error('获取插件列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGateways();
    const timer = setInterval(fetchGateways, 5000);
    return () => clearInterval(timer);
  }, [fetchGateways]);

  useEffect(() => {
    pluginApi.builtins().then((res: any) => setBuiltins(res.items || [])).catch(() => {});
  }, []);

  const handleStart = async (name: string) => {
    try {
      await pluginApi.start(name);
      message.success(`${name} 已启动`);
      fetchGateways();
    } catch {
      message.error('启动失败');
    }
  };

  const handleStop = async (name: string) => {
    try {
      await pluginApi.stop(name);
      message.success(`${name} 已停止`);
      fetchGateways();
    } catch {
      message.error('停止失败');
    }
  };

  const handleViewLogs = async (name: string) => {
    try {
      const res: any = await pluginApi.logs(name);
      setLogsModal({ visible: true, name, logs: res.logs || [] });
    } catch {
      message.error('获取日志失败');
    }
  };

  const handleRefreshLogs = async () => {
    try {
      const res: any = await pluginApi.logs(logsModal.name);
      setLogsModal(prev => ({ ...prev, logs: res.logs || [] }));
    } catch {
      message.error('刷新日志失败');
    }
  };

  const handleClearLogs = async () => {
    try {
      await pluginApi.clearLogs(logsModal.name);
      setLogsModal(prev => ({ ...prev, logs: [] }));
    } catch {
      message.error('清空日志失败');
    }
  };

  const handleEditConfig = async (gw: Gateway) => {
    try {
      const res: any = await pluginApi.getConfigFile(gw.name);
      const gc = res.global_config || null;
      setConfigModal({ visible: true, name: gw.name, globalConfigText: gc ? JSON.stringify(gc, null, 2) : '', gw });
      editForm.setFieldsValue({ working_dir: gw.working_dir, command: gw.command.join(' '), auto_start: gw.auto_start });
    } catch {
      message.error('获取配置失败');
    }
  };

  const handleSaveConfig = async () => {
    let globalConfig;
    try {
      globalConfig = JSON.parse(configModal.globalConfigText);
    } catch {
      message.error('JSON 格式错误，请检查后重试');
      return;
    }
    try {
      await pluginApi.saveConfigFile(configModal.name, globalConfig);
      message.success('配置已保存');
      setConfigModal({ visible: false, name: '', globalConfigText: '' });
    } catch {
      message.error('保存失败');
    }
  };

  const handleSaveEdit = async (values: any) => {
    try {
      const res: any = await pluginApi.getConfigFile(configModal.name);
      const updated = {
        ...(res.global_config || {}),
        working_dir: values.working_dir,
        command: shellSplit(values.command),
        auto_start: values.auto_start,
      };
      await pluginApi.saveConfigFile(configModal.name, updated);
      message.success('已保存');
      setConfigModal({ visible: false, name: '', globalConfigText: '' });
      fetchGateways();
    } catch {
      message.error('保存失败');
    }
  };

  const handleToggleAutoStart = async (gw: Gateway, checked: boolean) => {
    try {
      const res: any = await pluginApi.getConfigFile(gw.name);
      const updated = { ...(res.global_config || {}), auto_start: checked };
      await pluginApi.saveConfigFile(gw.name, updated);
      fetchGateways();
    } catch {
      message.error('更新失败');
    }
  };

  const handleAdd = async (values: any) => {
    try {
      const data = {
        ...values,
        command: shellSplit(values.command),
      };
      await pluginApi.add(data);
      message.success('插件已添加');
      setAddModal(false);
      form.resetFields();
      fetchGateways();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '添加失败');
    }
  };

  const handleDelete = async (name: string) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除插件 "${name}" 吗？这将停止运行中的进程。`,
      onOk: async () => {
        try {
          await pluginApi.delete(name);
          message.success(`${name} 已删除`);
          fetchGateways();
        } catch {
          message.error('删除失败');
        }
      },
    });
  };

  const statusMap: Record<string, { color: string; text: string }> = {
    running: { color: 'green', text: '运行中' },
    stopped: { color: 'default', text: '已停止' },
    restarting: { color: 'orange', text: '重启中' },
    crashed: { color: 'red', text: '已崩溃' },
  };

  return (
    <div className="apple-page plugin-page">
      <div className="apple-page-header plugin-page-header">
        <div>
          <h1 className="apple-page-title">插件配置</h1>
          <p className="apple-page-subtitle plugin-page-subtitle">管理插件网关进程、日志和配置文件</p>
        </div>
        <Space wrap>
          <Button className="apple-btn-create" icon={<PlusOutlined />} onClick={() => setAddModal(true)}>添加插件</Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        <div className="plugin-grid">
          {gateways.map((gw) => {
            const st = statusMap[gw.status] || { color: 'default', text: gw.status };
            return (
              <Card
                key={gw.name}
                hoverable
                className={`plugin-card plugin-card-${gw.status}`}
                actions={[
                  gw.status === 'running'
                    ? <Tooltip title="停止" key="toggle"><PauseCircleOutlined onClick={() => handleStop(gw.name)} /></Tooltip>
                    : <Tooltip title="启动" key="toggle"><PlayCircleOutlined onClick={() => handleStart(gw.name)} /></Tooltip>,
                  <Tooltip title="日志" key="logs"><FileTextOutlined onClick={() => handleViewLogs(gw.name)} /></Tooltip>,
                  <Tooltip title="编辑" key="config"><EditOutlined onClick={() => handleEditConfig(gw)} /></Tooltip>,
                  <Tooltip title="删除" key="del"><DeleteOutlined className="plugin-card-delete" onClick={() => handleDelete(gw.name)} /></Tooltip>,
                ]}
              >
                <div className="plugin-card-header">
                  <div className="plugin-card-title">
                    {gw.name}
                    <Tag color={st.color} style={{ marginLeft: 8, fontSize: 11 }}>{st.text}</Tag>
                  </div>
                </div>
                <div className="plugin-card-body">
                  <Tooltip title={gw.working_dir}>
                    <div className="plugin-card-field">
                      <span className="plugin-card-field-label">目录</span>
                      <span className="plugin-card-field-value">{gw.working_dir}</span>
                    </div>
                  </Tooltip>
                  <Tooltip title={gw.command.join(' ')}>
                    <div className="plugin-card-field">
                      <span className="plugin-card-field-label">命令</span>
                      <span className="plugin-card-field-value plugin-card-code">{gw.command.join(' ')}</span>
                    </div>
                  </Tooltip>
                  <div className="plugin-card-field">
                    <span className="plugin-card-field-label">PID</span>
                    <span className="plugin-card-field-value plugin-card-code">{gw.pid || '-'}</span>
                  </div>
                  <div className="plugin-card-field">
                    <span className="plugin-card-field-label">自启动</span>
                    <span className="plugin-card-field-value">
                      <Switch
                        size="small"
                        checked={gw.auto_start}
                        onChange={(checked) => handleToggleAutoStart(gw, checked)}
                      />
                    </span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </Spin>

      <Modal
        title={`日志 - ${logsModal.name}`}
        open={logsModal.visible}
        onCancel={() => setLogsModal({ visible: false, name: '', logs: [] })}
        footer={[
          <Button key="refresh" icon={<ReloadOutlined />} onClick={handleRefreshLogs}>刷新</Button>,
          <Button key="clear" icon={<ClearOutlined />} danger onClick={handleClearLogs}>清空</Button>,
          <Button key="close" onClick={() => setLogsModal({ visible: false, name: '', logs: [] })}>关闭</Button>,
        ]}
        width={900}
      >
        <div style={{ minHeight: 200, maxHeight: 500, overflow: 'auto', background: '#1e1e1e', padding: 12, borderRadius: 6 }}>
          <pre style={{ color: '#d4d4d4', fontSize: 12, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {logsModal.logs.length > 0 ? logsModal.logs.join('\n') : '暂无日志'}
          </pre>
        </div>
      </Modal>

      <Modal
        title={`编辑 - ${configModal.name}`}
        open={configModal.visible}
        onCancel={() => { setConfigModal({ visible: false, name: '', globalConfigText: '' }); editForm.resetFields(); }}
        footer={null}
        width={700}
      >
        <Tabs items={[
          {
            key: 'edit',
            label: '编辑',
            children: (
              <Form form={editForm} layout="vertical" onFinish={handleSaveEdit}>
                <Form.Item name="working_dir" label="工作目录">
                  <Input />
                </Form.Item>
                <Form.Item name="command" label="启动命令" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="auto_start" label="自动启动" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                  <Button type="primary" htmlType="submit">保存</Button>
                </Form.Item>
              </Form>
            ),
          },
          {
            key: 'config',
            label: '显示配置',
            children: (
              <>
                <TextArea
                  value={configModal.globalConfigText}
                  onChange={(e) => setConfigModal(prev => ({ ...prev, globalConfigText: e.target.value }))}
                  rows={16}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                  placeholder="未找到该插件的全局配置"
                />
                <div style={{ textAlign: 'right', marginTop: 8 }}>
                  <Button type="primary" onClick={handleSaveConfig}>保存</Button>
                </div>
              </>
            ),
          },
        ]} />
      </Modal>

      <Modal
        title="添加插件"
        open={addModal}
        onCancel={() => { setAddModal(false); form.resetFields(); setSelectedDesc(''); }}
        onOk={() => form.submit()}
        okText="添加"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" onFinish={handleAdd}>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入插件名称' }]}>
            <Input placeholder="例如: my-gateway" />
          </Form.Item>
          <Form.Item name="working_dir" label="工作目录">
            <AutoComplete
              placeholder="选择内置插件目录或输入自定义路径"
              options={builtins.map(b => ({ value: b.working_dir, label: b.name }))}
              onSelect={(val: string) => {
                const b = builtins.find(x => x.working_dir === val);
                if (b) {
                  const patch: any = {
                    working_dir: b.working_dir,
                    command: b.default_command || `python3 ${b.name}.py`,
                  };
                  if (!form.getFieldValue('name')) {
                    patch.name = b.name;
                  }
                  form.setFieldsValue(patch);
                  setSelectedDesc(b.description || '');
                }
              }}
            />
          </Form.Item>
          {selectedDesc && (
            <div style={{ marginTop: -8, marginBottom: 12, padding: '8px 10px', background: 'var(--apple-bg-secondary, #f5f5f5)', borderRadius: 6, fontSize: 12, color: 'var(--apple-text-secondary)', whiteSpace: 'pre-line' }}>
              {selectedDesc}
            </div>
          )}
          <Form.Item name="command" label="启动命令" initialValue="python3 main.py">
            <Input placeholder="python3 acp_proxy.py --port 52390" />
          </Form.Item>
          <Form.Item name="auto_start" label="自动启动" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default PluginManagement;
